/**
 * Where to send the user back after a token / subscription purchase.
 *
 * The buy-tokens journey leaves the flow page entirely
 * (`flow → /pricing → /checkout → external payment iframe → /payment/result`).
 * The external gateway redirect is a full browser navigation, so react-router
 * `location.state` is destroyed across the round-trip. We persist the origin
 * flow path in localStorage instead — the same reason `useFlowPersistence`
 * uses localStorage: it survives the gateway redirect, a mobile browser
 * kill/restore, and the email-confirm new tab.
 *
 * Guarded on consume by the SAME 24h TTL as flow drafts and by a strict
 * allowlist (the path's pathname must be exactly one of the registered flow
 * routes). Per the no-fallbacks policy an expired/foreign/garbage value
 * yields `null` so the caller falls back to its explicit default — a stale
 * value can never misroute the user.
 */

import { ALL_FLOW_KEYS } from "@/config/flowKeys";
import { FLOW_STATE_TTL_MS } from "@/hooks/useFlowPersistence";

const STORAGE_KEY = "purchase-return";

/** Every flow slug maps 1:1 to its route as `/<slug>` (see App.tsx). */
const FLOW_ROUTES = new Set(ALL_FLOW_KEYS.map((k) => `/${k}`));

interface StoredReturn {
  path: string;
  savedAt: number;
}

/** Remember the flow path the user is leaving to go buy credits. */
export function setPurchaseReturn(path: string): void {
  try {
    const record: StoredReturn = { path, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota / private mode — the user just won't be auto-returned. Harmless.
  }
}

/**
 * Read and clear the stored return path. Returns it only when it is fresh
 * (within the flow-draft TTL) AND its pathname is a registered flow route;
 * otherwise `null`.
 */
export function consumePurchaseReturn(): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const rec = JSON.parse(raw) as StoredReturn;
    if (typeof rec.path !== "string" || typeof rec.savedAt !== "number") {
      return null;
    }
    if (Date.now() - rec.savedAt > FLOW_STATE_TTL_MS) return null;
    const pathname = rec.path.split("?")[0].split("#")[0];
    return FLOW_ROUTES.has(pathname) ? rec.path : null;
  } catch {
    return null;
  }
}
